import {
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJInputSearch,
  NJPagination,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { AdminUser } from "@/types";
import { useAdminUsers, useDeleteAdminUser } from "./useAdminUsers";

const PAGE_SIZE = 20;

function ActionsCell({ data }: ICellRendererParams<AdminUser>) {
  const navigate = useNavigate();
  const deleteUser = useDeleteAdminUser();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!data) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      <NJIconButton
        icon="visibility"
        variant="tertiary"
        label={`View ${data.login}`}
        onClick={() => navigate(`/admin/users/${data.id}`)}
      />
      <NJIconButton
        icon="delete"
        variant="destructive"
        label={`Delete ${data.login}`}
        isDisabled={deleteUser.isPending}
        onClick={() => setConfirmOpen(true)}
      />
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete user"
        isPending={deleteUser.isPending}
        onConfirm={() =>
          deleteUser.mutate(data.id, { onSuccess: () => setConfirmOpen(false) })
        }
      >
        Delete user <strong>{data.email}</strong>? This action cannot be undone.
      </ConfirmModal>
    </div>
  );
}

/** Admin users table — server-side search + pagination. Reserved to `app-admin` (403 server-side). */
export function AdminUsersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [query]);

  const { data, isLoading, isError } = useAdminUsers({
    q: query,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  const columnDefs = useMemo<ColDef<AdminUser>[]>(
    () => [
      { headerName: "Login", field: "login", flex: 1 },
      { headerName: "Email", field: "email", flex: 1 },
      {
        headerName: "Name",
        valueGetter: (params) =>
          params.data ? `${params.data.firstName} ${params.data.lastName}` : "",
        flex: 1,
      },
      {
        headerName: "Groups",
        valueGetter: (params) => (params.data?.groups ?? []).join(", "),
        flex: 2,
      },
      {
        headerName: "Last login",
        field: "lastLoginAt",
        flex: 1,
        valueFormatter: (params) =>
          params.value ? new Date(params.value as string).toLocaleString() : "",
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCell,
        flex: 1,
        sortable: false,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <NJHeading as="h1" scale="lg">
        Administration — users
      </NJHeading>

      <NJInputSearch
        label="Search users"
        placeholder="login, email, group"
        value={searchInput}
        onChange={(event) => setSearchInput(event.currentTarget.value)}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <NJSpinner aria-label="Loading users" />
        </div>
      ) : isError ? (
        <NJInlineMessage variant="error">
          Could not load the users.
        </NJInlineMessage>
      ) : (
        <>
          <DataTable<AdminUser>
            rowData={data?.items ?? []}
            columnDefs={columnDefs}
          />
          {data && data.items.length === 0 && (
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              No user matches this search.
            </NJText>
          )}
          {totalPages > 1 && (
            <div className="flex justify-end">
              <NJPagination
                count={totalPages}
                currentItem={page}
                ariaLabelPrevious="Previous users page"
                ariaLabelNext="Next users page"
                onChange={(item: number) => setPage(item)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
